'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAccess } from '@repo/auth';
import type { TableConfigurationJson } from '@repo/schema/table-configuration';
import { focusManager, useQueryClient } from '@repo/tanstack/query';
import { createContext, type ReactNode, Suspense, use, useEffect, useState, useSyncExternalStore } from 'react';
import { usePathname, useRouter, useSearchParams } from '../table/adapters/next';
import { createTableConfigurationController, type TableConfigurationController } from './controller';
import type { TableConfigurationDefinition } from './definition';
type Controller = TableConfigurationController<TableConfigurationJson>;
const TableConfigurationContext = createContext<Controller | null>(null);
const TableConfigurationLocation = t0 => {
  const $ = _c(7);
  const {
    controller
  } = t0;
  const pathname = usePathname();
  const t1 = useSearchParams();
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toString();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const search = t2;
  let t3;
  let t4;
  if ($[2] !== controller || $[3] !== pathname || $[4] !== search) {
    t3 = () => controller.setLocation(pathname, search);
    t4 = [controller, pathname, search];
    $[2] = controller;
    $[3] = pathname;
    $[4] = search;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  return null;
};

/**
 * Owns one Table Configuration controller for a registered table. It may be mounted above the
 * table (for sidebar access). Outside the registered pathname it only loads pins and Default,
 * never a view and never the URL; without the table permission it makes no requests.
 */
export const TableConfigurationProvider = t0 => {
  const $ = _c(26);
  const {
    children,
    definition
  } = t0;
  const api = useApi();
  const queryClient = useQueryClient();
  const router = useRouter();
  let t1;
  if ($[0] !== definition) {
    t1 = () => createTableConfigurationController(definition);
    $[0] = definition;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [controller] = useState(t1);
  let t2;
  if ($[2] !== definition.permission) {
    t2 = {
      permissions: [definition.permission]
    };
    $[2] = definition.permission;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    allowed
  } = useAccess(t2);
  let t3;
  if ($[4] !== api.tableConfigurations.default || $[5] !== definition.tableKey) {
    t3 = JSON.stringify(api.tableConfigurations.default.queryKey({
      path: {
        tableKey: definition.tableKey
      }
    }));
    $[4] = api.tableConfigurations.default;
    $[5] = definition.tableKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const contextKey = t3;
  let t4;
  if ($[7] !== api.tableConfigurations || $[8] !== controller || $[9] !== queryClient || $[10] !== router) {
    t4 = () => {
      controller.connect({
        api: api.tableConfigurations,
        navigate: {
          push: href => router.push(href),
          replace: href_0 => router.replace(href_0)
        },
        queryClient
      });
    };
    $[7] = api.tableConfigurations;
    $[8] = controller;
    $[9] = queryClient;
    $[10] = router;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t4);
  let t5;
  let t6;
  if ($[12] !== allowed || $[13] !== contextKey || $[14] !== controller) {
    t5 = () => controller.setGate({
      allowed,
      contextKey
    });
    t6 = [allowed, contextKey, controller];
    $[12] = allowed;
    $[13] = contextKey;
    $[14] = controller;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[17] !== controller) {
    t7 = () => focusManager.subscribe(focused => {
      if (focused && controller.getSnapshot().view) {
        controller.refresh();
      }
    });
    t8 = [controller];
    $[17] = controller;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  useEffect(t7, t8);
  let t9;
  if ($[20] !== controller) {
    t9 = <Suspense fallback={null} data-sentry-element="Suspense" data-sentry-source-file="provider.tsx"><TableConfigurationLocation controller={controller} data-sentry-element="TableConfigurationLocation" data-sentry-source-file="provider.tsx" /></Suspense>;
    $[20] = controller;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== children || $[23] !== controller || $[24] !== t9) {
    t10 = <TableConfigurationContext value={controller} data-sentry-element="TableConfigurationContext" data-sentry-component="TableConfigurationProvider" data-sentry-source-file="provider.tsx">{t9}{children}</TableConfigurationContext>;
    $[22] = children;
    $[23] = controller;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};

/** The mounted controller, or `null` when no Table Configuration provider is mounted. */
export const useOptionalTableConfiguration = () => use(TableConfigurationContext);
export const useTableConfiguration = () => {
  const controller = useOptionalTableConfiguration();
  if (!controller) {
    throw new Error("useTableConfiguration must be used within TableConfigurationProvider");
  }
  return controller;
};
export const useTableConfigurationSnapshot = () => {
  const controller = useTableConfiguration();
  return useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getServerSnapshot);
};